/**
 * model/fasteners.ts — Holes → fastener anchors (STEP 5, kinematic chain).
 *
 * Layer `model/` (SPEC §6): transforms each feature hole into an anchor
 * fastener(`FastenerAnchor`). The position/axis in the world is calculated via the
 * FRAME of the parent lock (kinetic chain): `frame.localToWorld` for the
 * position, `frame.getWorldQuaternion` for the axis (hole.normal transformed).
 *
 * mesh/holes SEPARATION (FIX A): the frame quaternion is the SOLA
 * conversion (holesQuat) — holes DO NOT receive the body euler (which remains
 * on the GLB). So the normal [0,0,1] points along X (inside the beam) and the
 * hole [−41,45.95,0] remains on the connector plane (does not fly away).
 *
 * Fastener mapping (SPEC G10, AC-6):
 * - JOIST side holes → `joistFastenerItemId`
 * - HEADER side holes → `headFastenerItemId`
 * - holes `inclined` → `inclinedFastenerItemId` (inclined has PRIORITY
 * about type: a slanted hole always use slanted fastener)
 *
 * Chain order (SPEC §4.5, AC-8): STEP 5 (this module) is executed
 * BEFORE STEP 6 (notch). STEP 6 Δshift is a SEPARATE field
 * (`notchShift`) — Does NOT add to `position` here.
 *
 * Constrained 3D configurator: each function is commented for maintainability.*/

import * as THREE from "three";
import { COLORS } from "../config/colors";
import type { ConnectorSide, HoleFeature, HoleType, Vec3 } from "../data/types";
import type { FastenerAnchor, FastenerLegendEntry } from "./types";

/**
 * Input of `holeToFastenerAnchor` (PLAN §2.14, adapted to kinematic chain).
 *
 * ADAPTATION: instead of `parentQuaternion`/`parentPosition`/`holeOffsetInModule`
 * (scalar sums and manual conversions) the module receives the FRAME of the lock
 * parent: `frame.localToWorld` and `frame.getWorldQuaternion` do everything
 * (SPEC §6 point 2: no linear algebra reimplemented).
 *
 * `hole.position` is in the authoring frame (SSOT, not denied): for the header side
 * the mirroring Rz(180°) is already in the group frame (FIX A).*/
export interface FastenerInput {
  /** Fixed hole (parent connector features).*/
  hole: HoleFeature;
  /** 0-based index of the parent connector (c1=0, c2=1, …).*/
  connectorIndex: number;
  /** Parent connector side ("joist" | "header").*/
  side: ConnectorSide;
  /** FRAME of the parent lock (kinetic chain, THREE.Group already positioned).*/
  frame: THREE.Group;
  /** Item id fastener fixed (G10 mapping) or null if not available.*/
  fastenerItemId: string | null;
  /** Blob URL of the GLB vine; null → parametric fallback box (R6).*/
  glbUrl: string | null;
  /** Real fastener length (mm) from the catalog Fastener item's own `length`
   * column — the single source of truth also used server-side (eta_190831_lock.go).
   * Null when the selection/data isn't resolved yet, in which case
   * defaultLength is used instead.*/
  realLengthMm: number | null;
  /** Real fastener diameter (mm) from the catalog Fastener item's own
   * `diameter` column — the model represents the SCREW, not the hole, so this
   * wins over `hole.diameter_mm` (the clearance hole is wider). Null when the
   * selection isn't resolved yet, in which case the hole diameter is used.*/
  realDiameterMm: number | null;
  /** Default fastener length (config) if the item does not provide dims.*/
  defaultLength: number;
  /**
   * Δshift notch of STEP 6 inherited from the parent connector (SPEC G7/AC-3).
   * `[0,0,0]` if no beams cut on this side. Field SEPARATE from
   * position: applied ONLY in render.*/
  notchShift: Vec3;
  /**
   * Component euler (`ConnectorInstance.meshQuaternion`, 28-09-2026) — the
   * SAME local-frame rotation the GLB body receives (`ConnectorMesh.tsx`),
   * applied here to `hole.position`/`hole.normal` BEFORE `frame.localToWorld`.
   *
   * NOT the same thing FIX A forbids: FIX A keeps the euler OUT of the
   * shared frame quaternion that `connectorPlacement.ts` uses to derive the
   * header/joist mirror (that composition caused the historical mirroring
   * bug). This is a LOCAL pre-rotation of one hole's own coordinates, using
   * each side's own already-resolved `frame` for the world step — it never
   * touches the mirror derivation. Needed because `component_rotation_deg`
   * corrects a real mesh/hole-data authoring mismatch for some assets (the
   * 50/75/100/125 family): the mesh's own drilled cavities only line up
   * with `hole.position` once BOTH receive the same correction — applying
   * it to the mesh alone (the original 28-09-2026 fix) left the fastener
   * pointing at a bare wall, confirmed on 5 real assets by direct GLB vertex
   * measurement. Identity for every asset that doesn't need it (no
   * behavior change there).*/
  meshQuaternion: THREE.Quaternion;
}

/**
 * Lives ids available from the form (SPEC §3.2).*/
export interface FastenerIds {
  /** Header side fastener (headFastenerItemId).*/
  head: string | null;
  /** Joist side fastener (joistFastenerItemId).*/
  joist: string | null;
  /** Slanted hole fastener (inclinedFastenerItemId).*/
  inclined: string | null;
}

/**
 * SPEC G10 / AC-6: role mapping shared by fastenerItemIdFor and
 * fastenerLengthFor — the single source of truth for the rule, generic so
 * it serves both ids (string) and lengths (number) without duplicating the
 * branching.
 *
 * Inclined takes precedence over type: a `type: "inclined"` hole ALWAYS uses
 * the inclined value, regardless of side. Standard holes use the joist
 * value for the joist side and the head value for the header side.
 *
 * @param side Connector side ("joist" | "header").
 * @param holeType Hole type ("standard" | "inclined").
 * @param values Per-role values available from the form.
 * @returns The value resolved for this side/holeType combination.*/
function fastenerRoleValue<T>(side: ConnectorSide, holeType: HoleType, values: { head: T; joist: T; inclined: T }): T {
  if (holeType === "inclined") return values.inclined;
  return side === "joist" ? values.joist : values.head;
}

/**
 * SPEC G10 / AC-6: fastener mapping per side/hole type.
 * @param side Connector side ("joist" | "header").
 * @param holeType Hole type ("standard" | "inclined").
 * @param ids Life ids available from the form.
 * @returns Item id fastener resolved, or null if not available.*/
export function fastenerItemIdFor(side: ConnectorSide, holeType: HoleType, ids: FastenerIds): string | null {
  return fastenerRoleValue(side, holeType, ids);
}

/**
 * Real fastener lengths (mm) available from the form, per role — mirrors
 * FastenerIds but for the catalog Fastener item's own `length` instead of
 * its id.*/
export interface FastenerLengths {
  head: number | null;
  joist: number | null;
  inclined: number | null;
}

/**
 * Resolves the real length (mm) for a hole's role, using the same
 * side/holeType rule as fastenerItemIdFor. Null means unresolved — callers
 * fall back to a default length.
 * @param side Connector side ("joist" | "header").
 * @param holeType Hole type ("standard" | "inclined").
 * @param lengths Real lengths available from the form.*/
export function fastenerLengthFor(side: ConnectorSide, holeType: HoleType, lengths: FastenerLengths): number | null {
  return fastenerRoleValue(side, holeType, lengths);
}

/**
 * Real fastener diameters (mm) available from the form, per role — mirrors
 * FastenerLengths but for the catalog Fastener item's own `diameter`.*/
export interface FastenerDiameters {
  head: number | null;
  joist: number | null;
  inclined: number | null;
}

/**
 * Resolves the real diameter (mm) for a hole's role, same side/holeType rule
 * as fastenerItemIdFor. Null means unresolved — callers fall back to the
 * hole's own diameter.
 * @param side Connector side ("joist" | "header").
 * @param holeType Hole type ("standard" | "inclined").
 * @param diameters Real diameters available from the form.*/
export function fastenerDiameterFor(
  side: ConnectorSide,
  holeType: HoleType,
  diameters: FastenerDiameters,
): number | null {
  return fastenerRoleValue(side, holeType, diameters);
}

/**
 * Catalog codes available from the form, per role — mirrors FastenerIds but
 * for the item's own `code`.*/
export interface FastenerCodes {
  head: string | null;
  joist: string | null;
  inclined: string | null;
}

/**
 * Prodotti vite distinti effettivamente in scena, col colore assegnato.
 * Il colore e' PER PRODOTTO, non per ruolo: due ruoli con lo stesso item id
 * finiscono sulla stessa voce, quindi sullo stesso colore. L'ordine e' fisso
 * (head, joist, inclinata) perche' un colore che cambia al variare della
 * geometria sarebbe illeggibile. I ruoli sono tre, quindi la palette basta.*/
export function buildFastenerLegend(
  ids: FastenerIds,
  codes: FastenerCodes,
  fasteners: FastenerAnchor[],
): FastenerLegendEntry[] {
  //Solo i prodotti che hanno almeno una vite renderizzata: un ruolo senza
  //fori non deve comparire in legenda.
  const rendered = new Set(fasteners.map((f) => f.fastenerItemId).filter((id): id is string => !!id));
  const legend: FastenerLegendEntry[] = [];
  for (const [itemId, code] of [
    [ids.head, codes.head],
    [ids.joist, codes.joist],
    [ids.inclined, codes.inclined],
  ] as const) {
    if (!itemId || !rendered.has(itemId) || legend.some((entry) => entry.itemId === itemId)) continue;
    legend.push({ itemId, code, color: COLORS.fastenerByProduct[legend.length] ?? COLORS.fastener });
  }
  return legend;
}

/** Colore di una vite dalla legenda; fallback al colore standard se assente.*/
export function fastenerColorFor(legend: FastenerLegendEntry[], itemId: string | null): string {
  return legend.find((entry) => entry.itemId === itemId)?.color ?? COLORS.fastener;
}

/**
 * SPEC G10 / AC-6: one hole solved → FastenerAnchor (STEP 5, PLAN §2.14).
 *
 * - `position`: frame.localToWorld(meshQuaternion ∘ hole.position) — kinematic
 * chain (including alphaDeg rotation of the pivot if joist side)
 * - `axis`: hole.normal, same meshQuaternion pre-rotation, transformed from
 * the worldQuaternion of the frame (normalized) — insertion direction + tilt
 * in the world.
 * FIX A still holds for the SHARED frame quaternion (conversion only, no
 * euler — that's what the header/joist mirror derivation depends on).
 * `meshQuaternion` here is a LOCAL pre-rotation of this one hole, identity
 * for assets that don't need it — see the field's own doc-comment.
 * - `diameterMm`: realDiameterMm (the selected Fastener's catalog diameter)
 * when known, else hole.diameter_mm as a fallback
 * - `lengthMm`: realLengthMm (the selected Fastener's own catalog length)
 * when known, else defaultLength as a fallback.
 * - `notchShift`: inherited STEP 6 Δshift (separated from position).
 *
 * ASSUMPTION (P13, SPEC NIT): `hole.position` is expressed in the frame
 * authoring/assembly (mock: module.position = [0,0,0], span
 * assembly.width 100). With real features verify that the frame remains
 * the authoring/assembly (NOT the single module).
 *
 * @param input Conversion parameters (FastenerInput).
 * @returns Still lives resolved (world, WITHOUT notchShift in position).*/
export function holeToFastenerAnchor(input: FastenerInput): FastenerAnchor {
  const {
    hole,
    connectorIndex,
    side,
    frame,
    fastenerItemId,
    glbUrl,
    realLengthMm,
    realDiameterMm,
    defaultLength,
    notchShift,
    meshQuaternion,
  } = input;

  //Position of the hole in the world: FIRST the component euler in LOCAL
  //space (same correction the mesh body gets, see meshQuaternion doc-comment
  //above — identity for assets that don't need it), THEN localToWorld of the
  //lock frame (STEP 5).
  const localPos = new THREE.Vector3(hole.position[0], hole.position[1], hole.position[2]).applyQuaternion(
    meshQuaternion,
  );
  const worldPos = localPos.clone();
  frame.localToWorld(worldPos);

  //Axis = hole.normal, SAME local euler pre-rotation, then the worldQuaternion of the frame (FIX A).
  const worldQuat = frame.getWorldQuaternion(new THREE.Quaternion());
  const axisVec = new THREE.Vector3(hole.normal[0], hole.normal[1], hole.normal[2])
    .applyQuaternion(meshQuaternion)
    .normalize()
    .applyQuaternion(worldQuat);

  return {
    id: `${connectorIndex}:${side}:${hole.id}`,
    connectorIndex,
    side,
    holeId: String(hole.id),
    holeType: hole.type,
    position: [worldPos.x, worldPos.y, worldPos.z],
    axis: [axisVec.x, axisVec.y, axisVec.z],
    diameterMm: realDiameterMm ?? hole.diameter_mm,
    lengthMm: realLengthMm ?? defaultLength,
    fastenerItemId,
    glbUrl,
    notchShift,
    localAnchorZMm: localPos.z,
  };
}
